import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCompletedMatches } from '../api/api';

const C = {
  red: '#E8192C',
  orange: '#FF6B2B',
  green: '#3FCB7E',
  bg: '#0C0905',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textMuted: '#5C554E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, sans-serif',
};

const PAGE_SIZE = 6;

const rankSlug = (rankName) => String(rankName || 'unranked').toLowerCase().replace(/ /g, '-');
const rankAssetCandidates = (rankName) => {
  const slug = rankSlug(rankName);
  if (slug === 'unranked') {
    return ['/ranks/unranked.png', '/ranks/Unranked.png', '/ranks/unranked.webp', '/ranks/unranked.svg', '/assets/ranks/unranked.png'];
  }
  return [`/ranks/${slug}.png`, `/ranks/${slug}.webp`, `/assets/ranks/${slug}.png`];
};

function fmtDate(iso) {
  if (!iso) return 'Unknown date';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function mapLabel(map) {
  return String(map || 'Unknown')
    .replace(/^de_/, '')
    .replace(/_/g, ' ')
    .toUpperCase();
}

function modeLabel(mode) {
  if (mode === 'custom') return 'Custom Lobby';
  return mode === 'wingman' ? 'Wingman' : '5v5';
}

function isCustomLobby(match) {
  return match?.source === 'custom_lobby' || match?.match_type === 'custom' || match?.mode === 'custom';
}

function RankImage({ rankName, size = 38 }) {
  const [sourceIndex, setSourceIndex] = useState(0);
  const sources = rankAssetCandidates(rankName);
  const src = sources[sourceIndex];

  useEffect(() => {
    setSourceIndex(0);
  }, [rankName]);

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      onError={() => setSourceIndex((current) => Math.min(current + 1, sources.length - 1))}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
}

function PlayerPopover({ player, anchorRect, onClose }) {
  const navigate = useNavigate();
  const popoverRef = useRef(null);
  const popover = player?.popover || {};
  const stats = player?.stats || {};
  const isBot = Boolean(player?.is_bot || player?.kind === 'bot');

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const handlePointer = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) onClose();
    };

    window.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handlePointer);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handlePointer);
    };
  }, [onClose]);

  if (!player || !anchorRect) return null;

  const width = 280;
  const left = Math.max(12, Math.min(window.innerWidth - width - 12, anchorRect.left + anchorRect.width / 2 - width / 2));
  const top = Math.max(12, Math.min(window.innerHeight - 260, anchorRect.bottom + 8));

  if (isBot) {
    const kda = [stats.kills, stats.deaths, stats.assists].every((value) => value != null)
      ? `${stats.kills} / ${stats.deaths} / ${stats.assists}`
      : '-';

    return (
      <div
        ref={popoverRef}
        role="dialog"
        aria-label={`${player.username} bot player summary`}
        className="rp-player-popover"
        style={{ left, top, width }}
      >
        <div className="rp-popover-header">
          <div className="rp-avatar rp-bot-avatar">BOT</div>
          <div>
            <div className="rp-popover-name">{player.username}</div>
            <div className="rp-popover-muted">Bot Player · Team {player.team || '-'}</div>
          </div>
        </div>

        <div className="rp-popover-stats">
          <div><strong>{kda}</strong><span>K / D / A</span></div>
          <div><strong>{stats.headshots ?? '-'}</strong><span>Headshots</span></div>
          <div><strong>{stats.hs_percent != null ? `${Math.round(stats.hs_percent)}%` : '-'}</strong><span>HS</span></div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`${player.username} player summary`}
      className="rp-player-popover"
      style={{ left, top, width }}
    >
      <div className="rp-popover-header">
        <div className="rp-avatar">{player.avatar_url ? <img src={player.avatar_url} alt="" /> : player.username?.slice(0, 1).toUpperCase()}</div>
        <div>
          <div className="rp-popover-name">{player.username}</div>
          <div className="rp-popover-muted">{popover.rank_name || 'Unranked'}</div>
        </div>
        <RankImage rankName={popover.rank_name || 'Unranked'} size={42} />
      </div>

      <div className="rp-popover-stats">
        <div><strong>{popover.win_rate ?? 0}%</strong><span>Win rate</span></div>
        <div><strong>{popover.matches_played ?? 0}</strong><span>Matches</span></div>
        <div><strong>{popover.elo ?? '-'}</strong><span>ELO</span></div>
      </div>

      <button
        className="rp-primary-btn"
        onClick={() => {
          onClose();
          if (player.profile_url) navigate(player.profile_url);
        }}
      >
        View profile
      </button>
    </div>
  );
}

function PlayerButton({ player, onOpen }) {
  const ref = useRef(null);
  const stats = player.stats || {};
  const isBot = Boolean(player.is_bot || player.kind === 'bot');
  const kda = [stats.kills, stats.deaths, stats.assists].every((value) => value != null)
    ? `${stats.kills} / ${stats.deaths} / ${stats.assists}`
    : '-';

  return (
    <button ref={ref} className="rp-player-row" onClick={() => onOpen(player, ref.current)} type="button">
      <span className="rp-player-main">
        <span className={isBot ? 'rp-avatar rp-avatar-small rp-bot-avatar' : 'rp-avatar rp-avatar-small'}>
          {isBot ? 'BOT' : (player.avatar_url ? <img src={player.avatar_url} alt="" /> : player.username?.slice(0, 1).toUpperCase())}
        </span>
        <span>
          <span className="rp-player-name">{player.username}</span>
          <span className="rp-player-sub">{isBot ? 'Bot Player' : (player.popover?.rank_name || 'Unranked')}</span>
        </span>
      </span>
      <span className="rp-kda">{kda}</span>
      <span className={player.elo_change >= 0 ? 'rp-elo-positive' : 'rp-elo-negative'}>
        {player.elo_change == null ? '-' : `${player.elo_change >= 0 ? '+' : ''}${player.elo_change}`}
      </span>
    </button>
  );
}

function TeamPanel({ team, winner, onOpenPlayer }) {
  return (
    <section className={winner ? 'rp-team rp-team-winner' : 'rp-team'}>
      <div className="rp-team-head">
        <div>
          <div className="rp-eyebrow">{winner ? 'Winner' : `Team ${team.team}`}</div>
          <h3>{team.name || `Team ${team.team}`}</h3>
        </div>
        <div className="rp-team-score">{team.score}</div>
      </div>
      <div className="rp-score-head">
        <span>Player</span>
        <span>K / D / A</span>
        <span>ELO</span>
      </div>
      {(team.players || []).map((player) => (
        <PlayerButton key={player.user_id || player.username} player={player} onOpen={onOpenPlayer} />
      ))}
    </section>
  );
}

function MatchSummaryCard({ match, selected, onSelect }) {
  const team1 = match.teams?.[0] || { score: match.team1_score, players: [] };
  const team2 = match.teams?.[1] || { score: match.team2_score, players: [] };
  const mapImage = String(match.map || '').replace(/^de_/, '');
  const custom = isCustomLobby(match);

  return (
    <button className={selected ? 'rp-match-card rp-match-card-selected' : 'rp-match-card'} onClick={() => onSelect(match)} type="button">
      <span className="rp-map-thumb" style={{ backgroundImage: mapImage ? `url(/assets/maps/${mapImage}.jpg)` : 'none' }} />
      <span className="rp-card-main">
        <span className="rp-card-title">
          {mapLabel(match.map)}
          {custom && <span className="rp-custom-badge">Custom Lobby</span>}
        </span>
        <span className="rp-card-sub">{fmtDate(match.finished_at)} - {modeLabel(match.mode)}</span>
      </span>
      <span className="rp-card-score">{team1.score} - {team2.score}</span>
    </button>
  );
}

function MatchDetail({ match, onOpenPlayer }) {
  if (!match) {
    return (
      <section className="rp-detail-empty">
        <div className="rp-eyebrow">Select a match</div>
        <h2>Scoreboard waiting.</h2>
        <p>Choose a completed match to view teams, players, and the current demo status.</p>
      </section>
    );
  }

  const [team1, team2] = match.teams || [];

  return (
    <section className="rp-detail">
      <div className="rp-detail-hero">
        <div>
          <div className="rp-eyebrow">Completed match</div>
          <h2>{mapLabel(match.map)}<span>.</span></h2>
          <p>{fmtDate(match.finished_at)} - {modeLabel(match.mode)} {isCustomLobby(match) ? '· Custom Lobby' : ''}</p>
        </div>
        <div className="rp-final-score">
          <strong>{match.team1_score} - {match.team2_score}</strong>
          <span>Final score</span>
        </div>
        <button className="rp-demo-btn" disabled title="Demo downloads are coming soon">
          Download demo - Coming soon
        </button>
      </div>

      <div className="rp-teams-grid">
        {team1 && <TeamPanel team={team1} winner={match.winner_team === 1} onOpenPlayer={onOpenPlayer} />}
        {team2 && <TeamPanel team={team2} winner={match.winner_team === 2} onOpenPlayer={onOpenPlayer} />}
      </div>
    </section>
  );
}

export default function Matches() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState('');
  const [meta, setMeta] = useState({ total: 0, total_pages: 0, has_next: false, has_previous: false });
  const [selectedId, setSelectedId] = useState(matchId || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [popover, setPopover] = useState(null);

  const selectedMatch = useMemo(
    () => matches.find((match) => match.id === selectedId) || matches.find((match) => match.id === matchId) || matches[0] || null,
    [matchId, matches, selectedId]
  );

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setError('');
    setMeta({ total: 0, total_pages: 0, has_next: false, has_previous: page > 1 });
    try {
      const response = await getCompletedMatches({ page, limit: PAGE_SIZE, mode: mode || undefined });
      const data = response?.data || response;
      const list = Array.isArray(data?.matches) ? data.matches : [];
      setMatches(list);
      setMeta({
        total: data?.total || 0,
        total_pages: data?.total_pages || 0,
        has_next: Boolean(data?.has_next),
        has_previous: Boolean(data?.has_previous),
      });
      if (list[0]?.id) {
        setSelectedId((current) => current || list[0].id);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load completed matches.');
      setMatches([]);
      setMeta({ total: 0, total_pages: 0, has_next: false, has_previous: false });
    } finally {
      setLoading(false);
    }
  }, [mode, page]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    if (matchId) setSelectedId(matchId);
  }, [matchId]);

  const handleSelect = (match) => {
    setSelectedId(match.id);
    navigate(`/matches/${match.id}`, { replace: false });
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setPage(1);
    setMatches([]);
    setMeta({ total: 0, total_pages: 0, has_next: false, has_previous: false });
    setSelectedId(null);
    setPopover(null);
    navigate('/matches', { replace: true });
  };

  const handleOpenPlayer = (player, element) => {
    setPopover({ player, anchorRect: element.getBoundingClientRect() });
  };

  return (
    <div className="rp-matches-page">
      <style>{`
        .rp-matches-page{max-width:1440px;margin:0 auto;padding:32px;color:${C.text};font-family:${F.body};animation:rp-page-up 420ms cubic-bezier(.2,.7,.2,1) both}
        .rp-matches-page *{box-sizing:border-box}
        @keyframes rp-page-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @media (prefers-reduced-motion:reduce){.rp-matches-page *{animation-duration:1ms!important;transition-duration:1ms!important}}
        .rp-matches-head{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;padding-bottom:24px;border-bottom:1px solid ${C.border};margin-bottom:24px}
        .rp-eyebrow{font-size:10px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:${C.textDim};font-family:${F.body}}
        .rp-matches-head h1,.rp-detail h2,.rp-detail-empty h2,.rp-team h3{font-family:${F.display};text-transform:uppercase;margin:0;color:${C.text};letter-spacing:1.5px;line-height:.95}
        .rp-matches-head h1{font-size:60px;margin-top:8px}.rp-matches-head h1 span,.rp-detail h2 span{color:${C.red}}
        .rp-matches-head p,.rp-detail-hero p,.rp-detail-empty p{margin:8px 0 0;color:${C.textDim};font-weight:600;letter-spacing:.8px}
        .rp-mode-tabs{display:flex;border:1px solid ${C.border};background:${C.card}}
        .rp-mode-tabs button{background:transparent;border:0;border-left:1px solid ${C.border};color:${C.textDim};cursor:pointer;font-family:${F.display};font-size:13px;letter-spacing:2px;padding:10px 16px;text-transform:uppercase}
        .rp-mode-tabs button:first-child{border-left:0}.rp-mode-tabs button.active{background:${C.red};color:${C.text}}
        .rp-layout{display:grid;grid-template-columns:minmax(320px,420px) 1fr;gap:20px;align-items:start}
        .rp-list-panel,.rp-detail,.rp-detail-empty{background:${C.card};border:1px solid ${C.border};border-left:2px solid ${C.red}}
        .rp-list-panel{padding:14px;display:flex;flex-direction:column;gap:10px}
        .rp-list-meta{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:2px 2px 8px;color:${C.textDim};font-size:12px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase}
        .rp-match-card{display:flex;align-items:center;gap:12px;width:100%;padding:12px;background:${C.cardAlt};border:1px solid ${C.border};border-left:2px solid ${C.borderBright};color:inherit;cursor:pointer;text-align:left;transition:background 160ms ease,border-color 160ms ease}
        .rp-match-card:hover,.rp-match-card-selected{background:#1a130f;border-color:${C.borderBright};border-left-color:${C.red}}
        .rp-map-thumb{width:70px;height:52px;background:${C.bg};background-size:cover;background-position:center;flex-shrink:0;border:1px solid ${C.border};position:relative}
        .rp-map-thumb:after{content:'';position:absolute;inset:0;background:linear-gradient(0deg,rgba(12,9,5,.72),rgba(12,9,5,.08))}
        .rp-card-main{display:flex;flex-direction:column;gap:3px;min-width:0;flex:1}.rp-card-title{font-family:${F.display};font-size:19px;letter-spacing:1.5px;text-transform:uppercase}.rp-card-sub{font-size:11px;color:${C.textDim};font-weight:700;letter-spacing:1px;text-transform:uppercase}
        .rp-custom-badge{display:inline-flex;margin-left:8px;transform:translateY(-2px);padding:3px 7px;border:1px solid ${C.orange};color:${C.orange};font-family:${F.body};font-size:9px;font-weight:800;letter-spacing:1.5px;line-height:1;text-transform:uppercase;vertical-align:middle}
        .rp-card-score{font-family:${F.display};font-size:24px;letter-spacing:1.5px;font-variant-numeric:tabular-nums;white-space:nowrap}
        .rp-pagination{display:grid;grid-template-columns:1fr auto 1fr;gap:8px;margin-top:4px;align-items:center}.rp-pagination span{text-align:center;color:${C.textDim};font-size:12px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase}
        .rp-pagination button,.rp-primary-btn,.rp-demo-btn{font-family:${F.display};font-size:13px;letter-spacing:2px;text-transform:uppercase;padding:10px 14px;cursor:pointer}
        .rp-pagination button,.rp-primary-btn{background:${C.red};color:${C.text};border:0}.rp-pagination button:disabled{background:${C.cardAlt};color:${C.textMuted};cursor:not-allowed}
        .rp-detail,.rp-detail-empty{padding:20px;min-height:520px}.rp-detail-empty{display:flex;flex-direction:column;justify-content:center;align-items:flex-start}
        .rp-detail-hero{display:grid;grid-template-columns:1fr auto auto;gap:16px;align-items:center;padding-bottom:18px;border-bottom:1px solid ${C.border};margin-bottom:18px}.rp-detail h2{font-size:44px;margin-top:8px}
        .rp-final-score{display:flex;flex-direction:column;align-items:flex-end}.rp-final-score strong{font-family:${F.display};font-size:40px;letter-spacing:1.5px}.rp-final-score span{color:${C.textDim};font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase}
        .rp-demo-btn{background:transparent;color:${C.textMuted};border:1px solid ${C.borderBright};cursor:not-allowed}
        .rp-teams-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}.rp-team{background:${C.cardAlt};border:1px solid ${C.border};border-left:2px solid ${C.borderBright};overflow:hidden}.rp-team-winner{border-left-color:${C.green}}
        .rp-team-head{display:flex;align-items:center;justify-content:space-between;padding:16px;border-bottom:1px solid ${C.border}}.rp-team h3{font-size:26px;margin-top:5px}.rp-team-score{font-family:${F.display};font-size:42px;color:${C.text}}
        .rp-score-head,.rp-player-row{display:grid;grid-template-columns:1fr 90px 58px;gap:12px;align-items:center}.rp-score-head{padding:9px 14px;color:${C.textMuted};font-size:10px;font-weight:700;letter-spacing:1.8px;text-transform:uppercase;border-bottom:1px solid ${C.border}}
        .rp-player-row{width:100%;background:transparent;border:0;border-bottom:1px solid ${C.border};color:${C.text};cursor:pointer;padding:11px 14px;text-align:left}.rp-player-row:hover,.rp-player-row:focus{background:rgba(255,255,255,.035);outline:none}
        .rp-player-main{display:flex;align-items:center;gap:10px;min-width:0}.rp-avatar{width:42px;height:42px;background:${C.bg};border:1px solid ${C.borderBright};display:flex;align-items:center;justify-content:center;overflow:hidden;font-family:${F.display};color:${C.text};flex-shrink:0}.rp-avatar-small{width:34px;height:34px}.rp-avatar img{width:100%;height:100%;object-fit:cover}
        .rp-bot-avatar{border-color:${C.orange};color:${C.orange};font-size:11px;letter-spacing:1.2px;background:rgba(255,107,43,.08)}
        .rp-player-name,.rp-player-sub{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.rp-player-name{font-family:${F.display};font-size:16px;letter-spacing:1.2px;text-transform:uppercase}.rp-player-sub{color:${C.textDim};font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;margin-top:2px}.rp-kda,.rp-elo-positive,.rp-elo-negative{font-family:${F.display};font-size:16px;letter-spacing:1px;text-align:right;font-variant-numeric:tabular-nums}.rp-elo-positive{color:${C.green}}.rp-elo-negative{color:${C.red}}
        .rp-player-popover{position:fixed;z-index:500;background:${C.card};border:1px solid ${C.borderBright};border-left:2px solid ${C.red};box-shadow:0 16px 40px rgba(0,0,0,.68);padding:14px}
        .rp-popover-header{display:grid;grid-template-columns:auto 1fr auto;gap:12px;align-items:center}.rp-popover-name{font-family:${F.display};font-size:21px;letter-spacing:1.5px;text-transform:uppercase}.rp-popover-muted{color:${C.textDim};font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;margin-top:2px}
        .rp-popover-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin:14px 0}.rp-popover-stats div{background:${C.cardAlt};border:1px solid ${C.border};padding:10px;text-align:center}.rp-popover-stats strong{display:block;font-family:${F.display};font-size:22px}.rp-popover-stats span{display:block;color:${C.textDim};font-size:9px;font-weight:700;letter-spacing:1.6px;text-transform:uppercase;margin-top:3px}
        .rp-primary-btn{width:100%}.rp-state{padding:22px;background:${C.cardAlt};border:1px solid ${C.border};color:${C.textDim};font-weight:700;letter-spacing:1px}
        @media (max-width:1100px){.rp-layout,.rp-teams-grid{grid-template-columns:1fr}.rp-detail-hero{grid-template-columns:1fr}.rp-final-score{align-items:flex-start}}
        @media (max-width:720px){.rp-matches-page{padding:24px 18px}.rp-matches-head{align-items:flex-start;flex-direction:column}.rp-matches-head h1{font-size:48px}.rp-card-score{font-size:20px}.rp-score-head,.rp-player-row{grid-template-columns:1fr 68px 48px}.rp-map-thumb{display:none}}
      `}</style>

      <header className="rp-matches-head">
        <div>
          <div className="rp-eyebrow">Match history hub</div>
          <h1>Matches<span>.</span></h1>
          <p>Browse your completed matches, check scoreboards, and jump to player profiles.</p>
        </div>
        <div className="rp-mode-tabs" aria-label="Match mode filter">
          {[
            { id: '', label: 'All' },
            { id: 'fivevfive', label: '5v5' },
            { id: 'wingman', label: 'Wingman' },
            { id: 'custom', label: 'Custom' },
          ].map((tab) => (
            <button
              key={tab.id || 'all'}
              className={mode === tab.id ? 'active' : ''}
              aria-pressed={mode === tab.id}
              onClick={() => handleModeChange(tab.id)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="rp-layout">
        <aside className="rp-list-panel">
          <div className="rp-list-meta">
            <span>{meta.total} completed</span>
            <span>Page {page}{meta.total_pages ? ` / ${meta.total_pages}` : ''}</span>
          </div>

          {loading ? (
            <div className="rp-state">Loading completed matches...</div>
          ) : error ? (
            <div className="rp-state">{error}</div>
          ) : matches.length === 0 ? (
            <div className="rp-state">No completed matches yet.</div>
          ) : (
            matches.map((match) => (
              <MatchSummaryCard key={match.id} match={match} selected={selectedMatch?.id === match.id} onSelect={handleSelect} />
            ))
          )}

          <div className="rp-pagination">
            <button type="button" disabled={!meta.has_previous || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
            <span>{PAGE_SIZE} per page</span>
            <button type="button" disabled={!meta.has_next || loading} onClick={() => setPage((current) => current + 1)}>Next</button>
          </div>
        </aside>

        <MatchDetail match={selectedMatch} onOpenPlayer={handleOpenPlayer} />
      </main>

      {popover && (
        <PlayerPopover
          player={popover.player}
          anchorRect={popover.anchorRect}
          onClose={() => setPopover(null)}
        />
      )}
    </div>
  );
}
